import React from 'react';
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CheckCircle2, Loader2, MoreHorizontal, Trash, User, X, XCircle } from "lucide-react";

interface Participant {
  id: string;
  name: string;
  email: string;
  phone?: string;
  status: "signed_up" | "attended" | "no_show" | "opt_out";
}

interface ParticipantTableProps {
  participants: Participant[];
  selectedParticipants: string[];
  updatingParticipants: { [key: string]: boolean };
  eventStatus: string;
  onStatusUpdate: (participantId: string, status: "signed_up" | "attended" | "no_show" | "opt_out") => void;
  onParticipantSelect: (participantId: string) => void;
  onSelectAll: (checked: boolean) => void;
}

export function ParticipantTable({
  participants,
  selectedParticipants,
  updatingParticipants,
  eventStatus,
  onStatusUpdate,
  onParticipantSelect,
  onSelectAll,
}: ParticipantTableProps) {
  const canShowActions = eventStatus === 'event_in_progress' || eventStatus === 'event_complete';

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[30px] text-center">
              <Checkbox 
                checked={selectedParticipants.length > 0 && 
                  selectedParticipants.length === participants.length}
                onCheckedChange={onSelectAll}
              />
            </TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {participants.map((participant) => (
            <TableRow key={participant.id}>
              <TableCell className="text-center">
                <Checkbox 
                  checked={selectedParticipants.includes(participant.id)}
                  onCheckedChange={() => onParticipantSelect(participant.id)}
                />
              </TableCell>
              <TableCell>{participant.name}</TableCell>
              <TableCell>{participant.email}</TableCell>
              <TableCell>{participant.phone || '-'}</TableCell>
              <TableCell>
                <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                  participant.status === 'attended' ? 'bg-green-50 text-green-700' :
                  participant.status === 'signed_up' ? 'bg-blue-50 text-blue-700' :
                  participant.status === 'no_show' ? 'bg-red-50 text-red-700' :
                  'bg-orange-50 text-orange-700'
                }`}>
                  {participant.status === 'signed_up' ? 'Signed Up' :
                   participant.status === 'attended' ? 'Attended' :
                   participant.status === 'no_show' ? 'No Show' : 'Opted Out'}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  {canShowActions && participant.status === 'signed_up' && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 bg-green-50 text-green-700 border-green-200 hover:bg-green-100 hover:text-green-800"
                        onClick={() => onStatusUpdate(participant.id, 'attended')}
                        disabled={updatingParticipants[participant.id]}
                      >
                        {updatingParticipants[participant.id] ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                            Attended
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 bg-red-50 text-red-700 border-red-200 hover:bg-red-100 hover:text-red-800"
                        onClick={() => onStatusUpdate(participant.id, 'no_show')}
                        disabled={updatingParticipants[participant.id]}
                      >
                        {updatingParticipants[participant.id] ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3.5 w-3.5 mr-1" />
                            No Show
                          </>
                        )}
                      </Button>
                    </>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        disabled={updatingParticipants[participant.id]}
                      >
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      {canShowActions && participant.status === 'signed_up' && (
                        <>
                          <DropdownMenuItem 
                            onClick={() => onStatusUpdate(participant.id, 'attended')}
                            disabled={updatingParticipants[participant.id]}
                          >
                            {updatingParticipants[participant.id] ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Updating...
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Mark as Attended
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => onStatusUpdate(participant.id, 'no_show')}
                            disabled={updatingParticipants[participant.id]}
                          >
                            {updatingParticipants[participant.id] ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Updating...
                              </>
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 mr-2" />
                                Mark as No Show
                              </>
                            )}
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600">
                        <Trash className="h-4 w-4 mr-2" />
                        Remove Participant
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
